import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  simulateVerification,
  alertMessages,
  knownStreamUrls,
  type StreamVerificationResult,
  type AlertMessage,
} from '@/mocks/streamVerification';

type VerifyPhase = 'idle' | 'connecting' | 'checking' | 'done';

const phaseLabels: Record<VerifyPhase, string> = {
  idle: '',
  connecting: 'Conectando al servidor...',
  checking: 'Analizando stream de audio...',
  done: '',
};

export default function AdminSettingsStream() {
  const [streamUrl, setStreamUrl] = useState(knownStreamUrls[0].url);
  const [phase, setPhase] = useState<VerifyPhase>('idle');
  const [result, setResult] = useState<StreamVerificationResult | null>(null);
  const [alert, setAlert] = useState<AlertMessage | null>(null);
  const [history, setHistory] = useState<StreamVerificationResult[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const urlInputRef = useRef<HTMLInputElement>(null);
  const verifyInProgress = phase !== 'idle' && phase !== 'done';

  const handleVerify = async () => {
    if (verifyInProgress) return;

    const trimmed = streamUrl.trim();
    if (!trimmed) return;

    setPhase('connecting');
    setResult(null);
    setAlert(null);

    setTimeout(() => {
      setPhase('checking');
    }, 1200);

    try {
      const res = await simulateVerification(trimmed);
      setPhase('done');
      setResult(res);
      const matchedAlert = alertMessages.find((a) => a.type === res.alert) || null;
      setAlert(matchedAlert);
      setHistory((prev) => [res, ...prev].slice(0, 10));
    } catch {
      setPhase('idle');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleVerify();
    }
  };

  const handleSelectKnown = (url: string) => {
    setStreamUrl(url);
    setShowDropdown(false);
    setResult(null);
    setAlert(null);
    setPhase('idle');
  };

  const statusConfig = !result
    ? null
    : result.status === 'online'
      ? {
          label: 'Online',
          icon: 'ri-checkbox-circle-fill',
          dotClass: 'bg-emerald-500',
          textClass: 'text-emerald-600',
          bgClass: 'bg-emerald-50 border-emerald-200',
          badgeClass: 'bg-emerald-100 text-emerald-700',
        }
      : result.status === 'offline'
        ? {
            label: 'Offline',
            icon: 'ri-close-circle-fill',
            dotClass: 'bg-amber-500',
            textClass: 'text-amber-600',
            bgClass: 'bg-amber-50 border-amber-200',
            badgeClass: 'bg-amber-100 text-amber-700',
          }
        : {
            label: 'Error',
            icon: 'ri-error-warning-fill',
            dotClass: 'bg-red-500',
            textClass: 'text-red-600',
            bgClass: 'bg-red-50 border-red-200',
            badgeClass: 'bg-red-100 text-red-700',
          };

  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-radar-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Verificar Stream</h1>
              <p className="text-foreground-400 text-[9px]">Diagnóstico de transmisión</p>
            </div>
          </div>
          <Link
            to="/admin/settings"
            className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors"
          >
            <i className="ri-arrow-left-line text-xs"></i>
            Ajustes
          </Link>
        </div>
      </header>

      <div className="flex">


        <main className="flex-1 min-w-0">
          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div className="flex items-center gap-4">
              <Link
                to="/admin/settings"
                className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
              >
                <i className="ri-arrow-left-line text-foreground-500 text-sm"></i>
              </Link>
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Verificar Stream URL</h2>
                <p className="text-foreground-400 text-xs">
                  Probá y diagnosticá la transmisión de tu radio
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-1 rounded-lg bg-secondary-50 text-secondary-600 text-[10px] font-semibold border border-secondary-200">
                Sin backend
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 md:p-6 lg:p-8 pb-8">
            <div className="max-w-[800px] mx-auto space-y-6">

              {/* URL Input Section */}
              <div className="bg-background-50 border border-background-200 rounded-2xl p-5 lg:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-foreground-950 flex items-center justify-center shrink-0">
                    <i className="ri-link text-background-50 text-base"></i>
                  </div>
                  <div>
                    <h3 className="text-foreground-950 font-bold text-sm">Stream URL</h3>
                    <p className="text-foreground-400 text-[10px]">Ingresá la URL pública de tu transmisión</p>
                  </div>
                </div>

                <div className="relative">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        ref={urlInputRef}
                        type="text"
                        value={streamUrl}
                        onChange={(e) => {
                          setStreamUrl(e.target.value);
                          setResult(null);
                          setAlert(null);
                          setPhase('idle');
                        }}
                        onKeyDown={handleKeyDown}
                        placeholder="https://stream.turadio.com/live"
                        disabled={verifyInProgress}
                        className="w-full px-4 py-3 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-800 placeholder:text-foreground-300 font-mono outline-none focus:border-foreground-300 transition-colors disabled:opacity-50"
                      />
                      {streamUrl && !verifyInProgress && (
                        <button
                          onClick={() => {
                            setStreamUrl('');
                            setResult(null);
                            setAlert(null);
                            setPhase('idle');
                            urlInputRef.current?.focus();
                          }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                        >
                          <i className="ri-close-line text-foreground-400 text-xs"></i>
                        </button>
                      )}
                    </div>

                    <div className="relative">
                      <button
                        onClick={() => setShowDropdown(!showDropdown)}
                        className="w-10 h-[46px] rounded-xl bg-background-100 border border-background-200 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                        title="URLs conocidas"
                      >
                        <i className="ri-arrow-down-s-line text-foreground-500 text-sm"></i>
                      </button>

                      {showDropdown && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setShowDropdown(false)}
                          ></div>
                          <div className="absolute right-0 top-full mt-1 z-20 w-72 bg-background-50 border border-background-200 rounded-xl shadow-lg overflow-hidden">
                            <div className="px-3 py-2 border-b border-background-100">
                              <p className="text-foreground-400 text-[9px] font-semibold uppercase">URLs de prueba</p>
                            </div>
                            {knownStreamUrls.map((item) => (
                              <button
                                key={item.url}
                                onClick={() => handleSelectKnown(item.url)}
                                className={`w-full text-left px-3 py-2.5 flex items-center gap-2.5 cursor-pointer hover:bg-background-100 transition-colors ${
                                  streamUrl === item.url ? 'bg-primary-50' : ''
                                }`}
                              >
                                <i className={`${
                                  streamUrl === item.url ? 'ri-radio-button-fill text-primary-500' : 'ri-radio-button-line text-foreground-300'
                                } text-xs`}></i>
                                <div className="min-w-0">
                                  <p className={`text-xs font-medium truncate ${streamUrl === item.url ? 'text-primary-600' : 'text-foreground-700'}`}>
                                    {item.label}
                                  </p>
                                  <p className="text-foreground-400 text-[10px] truncate font-mono">{item.url}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleVerify}
                  disabled={!streamUrl.trim() || verifyInProgress}
                  className={`mt-4 w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                    verifyInProgress
                      ? 'bg-foreground-200 text-foreground-400 cursor-wait'
                      : !streamUrl.trim()
                        ? 'bg-foreground-100 text-foreground-300 cursor-not-allowed'
                        : 'bg-foreground-950 text-background-50 hover:bg-foreground-800 active:scale-[0.98]'
                  }`}
                >
                  {verifyInProgress ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base"></i>
                      <span>{phaseLabels[phase]}</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-play-fill text-base"></i>
                      <span>Probar transmisión</span>
                    </>
                  )}
                </button>

                {/* Phase progress */}
                {verifyInProgress && (
                  <div className="mt-4 flex items-center gap-2">
                    {(['connecting', 'checking'] as VerifyPhase[]).map((p, idx) => {
                      const phaseIndex = p === 'connecting' ? 0 : 1;
                      const currentIndex = phase === 'connecting' ? 0 : phase === 'checking' ? 1 : 2;
                      const isDone = phaseIndex < currentIndex;
                      const isCurrent = phaseIndex === currentIndex;

                      return (
                        <div key={p} className="flex items-center gap-2 flex-1">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-[10px] transition-all ${
                            isDone
                              ? 'bg-emerald-500 text-background-50'
                              : isCurrent
                                ? 'bg-foreground-950 text-background-50 animate-pulse'
                                : 'bg-background-200 text-foreground-400'
                          }`}>
                            {isDone ? (
                              <i className="ri-check-line text-[10px]"></i>
                            ) : (
                              <span className="text-[9px] font-bold">{idx + 1}</span>
                            )}
                          </div>
                          <span className={`text-[10px] font-medium whitespace-nowrap ${
                            isDone ? 'text-emerald-600' : isCurrent ? 'text-foreground-700' : 'text-foreground-300'
                          }`}>
                            {p === 'connecting' ? 'Conectar' : 'Analizar'}
                          </span>
                          {idx === 0 && (
                            <div className={`flex-1 h-px ${
                              currentIndex > 0 ? 'bg-emerald-300' : 'bg-background-200'
                            }`}></div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Result Section */}
              {result && alert && statusConfig && (
                <div className="space-y-5 animate-fade-in-up">
                  {/* Status Banner */}
                  <div className={`rounded-2xl border p-5 lg:p-6 ${statusConfig.bgClass}`}>
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${
                        result.status === 'online'
                          ? 'bg-emerald-500'
                          : result.status === 'offline'
                            ? 'bg-amber-500'
                            : 'bg-red-500'
                      }`}>
                        <i className={`${statusConfig.icon} text-background-50 text-xl`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-foreground-950 font-bold text-base">{alert.title}</h4>
                          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${statusConfig.badgeClass}`}>
                            {statusConfig.label}
                          </span>
                        </div>
                        <p className="text-foreground-500 text-xs leading-relaxed">{alert.description}</p>

                        {/* Online: live indicator */}
                        {result.status === 'online' && (
                          <div className="flex items-center gap-3 mt-4 flex-wrap">
                            <div className="flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                              <span className="text-emerald-600 text-[11px] font-semibold">{result.listeners} oyentes</span>
                            </div>
                            <span className="text-foreground-300 text-[10px]">·</span>
                            <span className="text-foreground-500 text-[11px]">Pico: {result.peakListeners}</span>
                            <span className="text-foreground-300 text-[10px]">·</span>
                            <span className="text-foreground-500 text-[11px]">{result.genre}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Key metrics row */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="bg-background-50 border border-background-200 rounded-xl p-4">
                      <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-1">Tiempo de respuesta</p>
                      <p className="text-foreground-950 font-bold text-lg font-mono">
                        {result.responseTime > 0 ? `${result.responseTime} ms` : '-'}
                      </p>
                      {result.responseTime > 0 && (
                        <p className={`text-[10px] font-medium mt-0.5 ${
                          result.responseTime < 150 ? 'text-emerald-500' : result.responseTime < 500 ? 'text-amber-500' : 'text-red-500'
                        }`}>
                          {result.responseTime < 150 ? 'Excelente' : result.responseTime < 500 ? 'Aceptable' : 'Lento'}
                        </p>
                      )}
                    </div>
                    <div className="bg-background-50 border border-background-200 rounded-xl p-4">
                      <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-1">Formato detectado</p>
                      <p className="text-foreground-950 font-bold text-lg font-mono">{result.detectedFormat}</p>
                      <p className="text-foreground-400 text-[10px] mt-0.5">{result.detectedBitrate}</p>
                    </div>
                    <div className="bg-background-50 border border-background-200 rounded-xl p-4">
                      <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-1">Codec de audio</p>
                      <p className="text-foreground-950 font-bold text-sm font-mono leading-tight">{result.audioCodec}</p>
                      <p className="text-foreground-400 text-[10px] mt-0.5">{result.channels} · {result.sampleRate}</p>
                    </div>
                    <div className="bg-background-50 border border-background-200 rounded-xl p-4">
                      <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-1">Servidor</p>
                      <p className="text-foreground-950 font-bold text-sm font-mono leading-tight">{result.serverType}</p>
                      <p className="text-foreground-400 text-[10px] mt-0.5">{result.contentType}</p>
                    </div>
                  </div>

                  {/* Technical Details */}
                  <div className="bg-background-50 border border-background-200 rounded-2xl p-5 lg:p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <i className="ri-code-s-slash-line text-foreground-500 text-sm"></i>
                      <h4 className="text-foreground-950 font-bold text-sm">Detalles técnicos</h4>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                      <DetailRow label="Stream Name" value={result.streamName} />
                      <DetailRow label="Última verificación" value={result.lastVerification} />
                      <DetailRow label="URL verificada" value={result.streamUrl} mono />
                      <DetailRow label="Tipo de contenido" value={result.contentType} />
                      <DetailRow label="Género" value={result.genre} />
                      <DetailRow label="Canales" value={result.channels} />
                      <DetailRow label="Sample Rate" value={result.sampleRate} />
                      <DetailRow
                        label="Info de canción"
                        value={result.metadataSupported ? 'Soportado' : 'No soportado'}
                        valueClass={result.metadataSupported ? 'text-emerald-600' : 'text-foreground-400'}
                      />
                      <DetailRow label="Estado del servidor" value={result.icecastUrl} mono />
                      <DetailRow label="Oyentes actuales" value={result.listeners > 0 ? result.listeners.toString() : '-'} />
                    </div>
                  </div>

                  {/* Verification History */}
                  {history.length > 1 && (
                    <div className="bg-background-50 border border-background-200 rounded-2xl p-5 lg:p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <i className="ri-history-line text-foreground-500 text-sm"></i>
                        <h4 className="text-foreground-950 font-bold text-sm">Historial de verificaciones</h4>
                        <span className="text-foreground-400 text-[10px]">({history.length})</span>
                      </div>
                      <div className="space-y-2">
                        {history.map((h, i) => {
                          const isLatest = i === 0;
                          const hConfig = h.status === 'online'
                            ? { dot: 'bg-emerald-500', text: 'text-emerald-600', label: 'Online' }
                            : h.status === 'offline'
                              ? { dot: 'bg-amber-500', text: 'text-amber-600', label: 'Offline' }
                              : { dot: 'bg-red-500', text: 'text-red-600', label: 'Error' };

                          return (
                            <div
                              key={i}
                              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                                isLatest ? 'bg-background-50 border border-background-200' : 'bg-background-50/50'
                              }`}
                            >
                              <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${hConfig.dot} ${isLatest ? 'animate-pulse' : ''}`}></div>
                              <div className="flex-1 min-w-0 flex items-center gap-3 flex-wrap">
                                <span className={`text-xs font-semibold ${hConfig.text}`}>{hConfig.label}</span>
                                <span className="text-foreground-300 text-[10px]">·</span>
                                <span className="text-foreground-500 text-[11px] font-mono truncate max-w-[280px]">{h.streamUrl}</span>
                              </div>
                              <div className="flex items-center gap-3 text-right shrink-0">
                                {h.responseTime > 0 && (
                                  <span className="text-foreground-400 text-[11px] font-mono">{h.responseTime} ms</span>
                                )}
                                <span className="text-foreground-300 text-[10px]">{h.lastVerification}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Empty state: idle without result */}
              {!result && phase === 'idle' && (
                <div className="bg-background-50 border border-background-200 rounded-2xl p-8 lg:p-10 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-radar-line text-foreground-400 text-2xl"></i>
                  </div>
                  <h4 className="text-foreground-700 font-semibold text-sm mb-2">Listo para verificar</h4>
                  <p className="text-foreground-400 text-xs max-w-[340px] mx-auto leading-relaxed">
                    Ingresá la URL de tu stream y hacé clic en <strong className="text-foreground-600">Probar transmisión</strong>.
                    El sistema va a conectarse, detectar el formato y verificar que el audio esté fluyendo correctamente.
                  </p>

                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 text-left">
                    <div className="p-3 rounded-xl bg-background-100">
                      <div className="w-7 h-7 rounded-lg bg-primary-100 flex items-center justify-center mb-2">
                        <i className="ri-plug-line text-primary-500 text-xs"></i>
                      </div>
                      <p className="text-foreground-700 text-[11px] font-semibold mb-0.5">Conexión</p>
                      <p className="text-foreground-400 text-[10px]">Verifica que el servidor responda y el puerto esté abierto.</p>
                    </div>
                    <div className="p-3 rounded-xl bg-background-100">
                      <div className="w-7 h-7 rounded-lg bg-accent-100 flex items-center justify-center mb-2">
                        <i className="ri-sound-module-line text-accent-500 text-xs"></i>
                      </div>
                      <p className="text-foreground-700 text-[11px] font-semibold mb-0.5">Formato</p>
                      <p className="text-foreground-400 text-[10px]">Detecta codec, bitrate, sample rate y canales del stream.</p>
                    </div>
                    <div className="p-3 rounded-xl bg-background-100">
                      <div className="w-7 h-7 rounded-lg bg-secondary-100 flex items-center justify-center mb-2">
                        <i className="ri-bar-chart-2-line text-secondary-500 text-xs"></i>
                      </div>
                      <p className="text-foreground-700 text-[11px] font-semibold mb-0.5">Métricas</p>
                      <p className="text-foreground-400 text-[10px]">Tiempo de respuesta, oyentes actuales y metadata del servidor.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Tips box — always visible */}
              <div className="bg-secondary-50 border border-secondary-200 rounded-2xl p-4 lg:p-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-xl bg-white flex items-center justify-center shrink-0">
                    <i className="ri-lightbulb-line text-secondary-500 text-sm"></i>
                  </div>
                  <div>
                    <h4 className="text-secondary-700 font-bold text-xs mb-1.5">Consejos para un stream confiable</h4>
                    <ul className="space-y-1">
                      <li className="flex items-start gap-1.5 text-secondary-600/80 text-[10px]">
                        <i className="ri-check-line text-secondary-500 text-[10px] mt-px shrink-0"></i>
                        Si usás tu propio servidor, asegurate de que sea dedicado y no un hosting compartido.
                      </li>
                      <li className="flex items-start gap-1.5 text-secondary-600/80 text-[10px]">
                        <i className="ri-check-line text-secondary-500 text-[10px] mt-px shrink-0"></i>
                        La calidad de audio recomendada es estándar (128 kbps) o alta (192 kbps).
                      </li>
                      <li className="flex items-start gap-1.5 text-secondary-600/80 text-[10px]">
                        <i className="ri-check-line text-secondary-500 text-[10px] mt-px shrink-0"></i>
                        Mantené tu software de emisión siempre actualizado para evitar cortes inesperados.
                      </li>
                      <li className="flex items-start gap-1.5 text-secondary-600/80 text-[10px]">
                        <i className="ri-check-line text-secondary-500 text-[10px] mt-px shrink-0"></i>
                        Si tu streaming es administrado por RFW, todo esto ya está cubierto automáticamente.
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </main>
      </div>


    </div>
  );
}

function DetailRow({
  label,
  value,
  mono,
  valueClass,
}: {
  label: string;
  value: string;
  mono?: boolean;
  valueClass?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-background-100 last:border-0">
      <span className="text-foreground-400 text-[10px] font-medium shrink-0">{label}</span>
      <span className={`text-xs font-medium text-right break-all ${valueClass || 'text-foreground-700'} ${mono ? 'font-mono' : ''}`}>
        {value}
      </span>
    </div>
  );
}